import { useCallback } from 'react';
import { usePreference } from '@frontend/user-preferences';

export const usePreferredOutboundNumberUserSettings = () => {
  const [preferredOutboundLocationPhone = {}, setPreferredOutboundLocationPhone] = usePreference(
    'PreferredOutboundLocationPhone'
  );

  /**
   * Get the preferred outbound number for a location.
   * Returns the phone number string, or empty string if no preference is set or was explicitly cleared.
   */
  const getPreferredOutboundNumberForLocation = useCallback(
    (locationId: string) => preferredOutboundLocationPhone[locationId] ?? '',
    [preferredOutboundLocationPhone]
  );

  /**
   * Check if the user has ever interacted with the preferred number setting for a location.
   * Returns true if user has set OR explicitly cleared the preference.
   * Returns false if no interaction has occurred (can auto-initialize).
   */
  const hasPreferredOutboundNumberInteraction = useCallback(
    (locationId: string) => locationId in preferredOutboundLocationPhone,
    [preferredOutboundLocationPhone]
  );

  const setPreferredOutboundNumberForLocation = useCallback(
    (locationId: string, locationPhone: string) => {
      const updatedPreference = {
        ...preferredOutboundLocationPhone,
        [locationId]: locationPhone,
      };
      setPreferredOutboundLocationPhone(updatedPreference);
    },
    [preferredOutboundLocationPhone, setPreferredOutboundLocationPhone]
  );

  /**
   * Clear the preferred number for a location.
   * Sets the value to empty string (instead of deleting) to track that user explicitly cleared it.
   * This prevents auto-initialization from re-setting the preference.
   */
  const clearPreferredOutboundNumberForLocation = useCallback(
    (locationId: string) => {
      const updatedPreference = {
        ...preferredOutboundLocationPhone,
        [locationId]: '', // Set to empty string instead of deleting - tracks explicit clear
      };
      setPreferredOutboundLocationPhone(updatedPreference);
    },
    [preferredOutboundLocationPhone, setPreferredOutboundLocationPhone]
  );

  return {
    getPreferredOutboundNumberForLocation,
    hasPreferredOutboundNumberInteraction,
    setPreferredOutboundNumberForLocation,
    clearPreferredOutboundNumberForLocation,
  };
};
